import { type VStackProps, VStack } from '@frontend/legacy-components';

import {
  ViewerAccordionPanel,
  ViewerAccordionPanelHeaderActionWrapper,
} from 'modules/common/viewer/components';

import { useDxfViewerSession } from '../DxfViewerContext';
import { DXF_Z_INDEX } from '../constants';
import { useDxfLayerPanel } from '../hooks/useDxfLayerPanel';
import { useDxfMappingPanel } from '../hooks/useDxfMappingPanel';
import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useDxfLoad } from '../session/useSessionState';
// import { DxfIssuesPanel } from './DxfIssuesPanel';
import { DxfLayerPanel } from './DxfLayerPanel';
import { DxfMappingPanel } from './DxfMappingPanel';
import { EditMappingButton } from './EditMappingButton';
import { ToggleColoringButton } from './ToggleColoringButton';

/** Nothing detects issues yet — see `MappingIssue`. */
// const NO_ISSUES = [] as const;

type DxfLayerPanelReadyProps = VStackProps & {
  session: DxfViewerSession;
  model: DrawingModel;
};

/**
 * The side panels, for the viewer's panel column.
 *
 * Assumes a ready drawing: `DxfSidePanelHost` already gates the column on
 * `load.status`.
 */
const DxfSidePanelsReady = ({
  session,
  model,
  ...props
}: DxfLayerPanelReadyProps) => {
  const { isLayerColoringOn, onToggleLayerColoring, ...layerPanel } =
    useDxfLayerPanel(session, model);
  const { isMappingColoringOn, onToggleMappingColoring, mode, ...panel } =
    useDxfMappingPanel(session);
  const showEditMapping = panel.hasMapping && mode !== 'mapping';
  return (
    <VStack
      position="absolute"
      top={2}
      left={2}
      bottom={2}
      zIndex={DXF_Z_INDEX.CHROME}
      spacing={0.5}
      align="stretch"
      w="17rem"
      pointerEvents="none"
      overflowY="auto"
      {...props}
    >
      <ViewerAccordionPanel
        title="Layers"
        headerAction={
          <ViewerAccordionPanelHeaderActionWrapper>
            <ToggleColoringButton
              isColoringOn={isLayerColoringOn}
              onToggleColoring={onToggleLayerColoring}
              hideColoringLabel="Hide layer colors"
              showColoringLabel="Show layer colors"
            />
          </ViewerAccordionPanelHeaderActionWrapper>
        }
      >
        <DxfLayerPanel {...layerPanel} />
      </ViewerAccordionPanel>
      <ViewerAccordionPanel
        title="Mapping"
        headerActionWidth={showEditMapping ? 7 : 4}
        headerAction={
          panel.hasMapping ? (
            <ViewerAccordionPanelHeaderActionWrapper
              spacing={showEditMapping ? 1 : 0}
            >
              <ToggleColoringButton
                isColoringOn={isMappingColoringOn}
                onToggleColoring={onToggleMappingColoring}
                hideColoringLabel="Hide mapping colors"
                showColoringLabel="Show mapping colors"
              />
              {showEditMapping ? (
                <EditMappingButton
                  isDisabled={!panel.canEnterMapping}
                  disabledReason={panel.lockReason}
                  onEnterMappingMode={panel.onEnterMappingMode}
                />
              ) : undefined}
            </ViewerAccordionPanelHeaderActionWrapper>
          ) : undefined
        }
      >
        <DxfMappingPanel {...panel} isMapping={mode === 'mapping'} />
      </ViewerAccordionPanel>

      {/* Issues describe a mapping, so there is nothing to say without one. */}
      {/* TODO: Uncomment this when issues are implemented */}
      {/* {panel.hasMapping && (
        <ViewerAccordionPanel title="Issues">
          <DxfIssuesPanel issues={NO_ISSUES} />
        </ViewerAccordionPanel>
      )} */}
    </VStack>
  );
};
DxfSidePanelsReady.displayName = 'DxfSidePanelsReady';

/**
 * Load gate for the side panels. Pages pass positioning only — they do not
 * branch on `session.load`.
 */
export const DxfSidePanelsHost = (props: VStackProps) => {
  const session = useDxfViewerSession();
  const load = useDxfLoad(session);
  if (load.status !== 'ready') {
    return null;
  }
  return <DxfSidePanelsReady session={session} model={load.model} {...props} />;
};
DxfSidePanelsHost.displayName = 'DxfSidePanelsHost';
